import { Eye, EyeOff } from 'lucide-react'
import { memo, useState } from 'react'
import { FaWordpress } from 'react-icons/fa'
import { Button } from '../ui/button'
import { Spinner } from '../ui/spinner'
import { OAuthButton } from './OAuthButton'

interface WordPressCredentialsFormProps {
  credentials: Record<string, string>
  isLoading: boolean
  isTesting: boolean
  isOAuthInProgress: boolean
  isSelfHosted: boolean
  allFieldsFilled: string | boolean | undefined
  editMode?: boolean
  onFieldChange: (key: string, value: string) => void
  onTest: () => void
  onConnect: () => void
  onWordPressOAuth: () => void
}

export const WordPressCredentialsForm = memo(function WordPressCredentialsForm({
  credentials,
  isLoading,
  isTesting,
  isOAuthInProgress,
  isSelfHosted,
  allFieldsFilled,
  editMode = false,
  onFieldChange,
  onTest,
  onConnect,
  onWordPressOAuth,
}: WordPressCredentialsFormProps) {
  const [showPassword, setShowPassword] = useState(false)

  return (
    <>
      {/* Site URL - always shown */}
      <div>
        <label htmlFor="siteUrl" className="mb-1 block text-sm font-medium text-black">
          Site URL
        </label>
        <p className="mb-2 text-sm text-black/60">Your WordPress site URL</p>
        <input
          id="siteUrl"
          type="text"
          value={credentials['siteUrl'] || ''}
          onChange={(e) => onFieldChange('siteUrl', e.target.value)}
          placeholder="https://yoursite.com"
          className="w-full rounded-2xl border border-[#E0E0E0] bg-[#F3F3F3] p-4 text-base text-black placeholder:text-gray-400 focus:border-[#B0B0B0] focus:outline-none"
          disabled={isLoading || isTesting || isOAuthInProgress}
        />
      </div>

      {/* Self-hosted checkbox */}
      <div>
        <label
          htmlFor="selfHosted"
          className="flex cursor-pointer items-start gap-3 rounded-2xl border border-[#E0E0E0] bg-[#F3F3F3] p-4"
        >
          <input
            id="selfHosted"
            type="checkbox"
            checked={credentials['selfHosted'] === 'true'}
            onChange={(e) => onFieldChange('selfHosted', e.target.checked.toString())}
            className="mt-0.5 h-5 w-5 cursor-pointer rounded border-gray-300 text-black focus:ring-2 focus:ring-black"
            disabled={isLoading || isTesting || isOAuthInProgress}
          />
          <div className="flex-1">
            <span className="block text-sm font-medium text-black">Self-Hosted WordPress</span>
            <p className="mt-1 text-sm text-black/60">
              Check this if your site is self-hosted (not WordPress.com)
            </p>
          </div>
        </label>
      </div>

      {/* Show username and password fields only for self-hosted */}
      {isSelfHosted && (
        <>
          <div>
            <label htmlFor="username" className="mb-1 block text-sm font-medium text-black">
              Username
            </label>
            <p className="mb-2 text-sm text-black/60">Your WordPress username</p>
            <input
              id="username"
              type="text"
              value={credentials['username'] || ''}
              onChange={(e) => onFieldChange('username', e.target.value)}
              placeholder="admin"
              className="w-full rounded-2xl border border-[#E0E0E0] bg-[#F3F3F3] p-4 text-base text-black placeholder:text-gray-400 focus:border-[#B0B0B0] focus:outline-none"
              disabled={isLoading || isTesting || isOAuthInProgress}
            />
          </div>

          <div>
            <label
              htmlFor="applicationPassword"
              className="mb-1 block text-sm font-medium text-black"
            >
              Application Password
            </label>
            <p className="mb-2 text-sm text-black/60">
              Create this in WordPress under Users → Profile → Application Passwords
            </p>
            <div className="relative">
              <input
                id="applicationPassword"
                type={showPassword ? 'text' : 'password'}
                value={credentials['applicationPassword'] || ''}
                onChange={(e) => onFieldChange('applicationPassword', e.target.value)}
                placeholder="xxxx xxxx xxxx xxxx xxxx xxxx"
                className="w-full rounded-2xl border border-[#E0E0E0] bg-[#F3F3F3] p-4 pr-20 text-base text-black placeholder:text-gray-400 focus:border-[#B0B0B0] focus:outline-none"
                disabled={isLoading || isTesting || isOAuthInProgress}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
                disabled={isLoading || isTesting || isOAuthInProgress}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Show OAuth button for WordPress.com, regular buttons for self-hosted */}
      {!isSelfHosted ? (
        <div className="mt-6">
          <OAuthButton
            onClick={onWordPressOAuth}
            disabled={isOAuthInProgress || !credentials['siteUrl']?.trim()}
            isLoading={isOAuthInProgress}
            icon={<FaWordpress className="h-5 w-5" />}
          >
            {isOAuthInProgress ? 'Connecting...' : 'Connect with WordPress.com'}
          </OAuthButton>
        </div>
      ) : (
        <div className="mt-6 flex gap-3">
          <Button
            onClick={onTest}
            disabled={!allFieldsFilled || isLoading || isTesting}
            variant="secondary"
            size="default"
            className="flex-1"
          >
            {isTesting ? (
              <>
                <Spinner size={16} />
                <span className="ml-2">Testing...</span>
              </>
            ) : (
              'Test Connection'
            )}
          </Button>

          <Button
            onClick={onConnect}
            disabled={!allFieldsFilled || isLoading || isTesting}
            size="default"
            className="flex-1"
          >
            {isLoading ? (
              <>
                <Spinner size={16} />
                <span className="ml-2">{editMode ? 'Updating...' : 'Connecting...'}</span>
              </>
            ) : editMode ? (
              'Update'
            ) : (
              'Connect'
            )}
          </Button>
        </div>
      )}
    </>
  )
})
